import React from 'react'
import gql from 'graphql-tag'
import { FieldValues } from '@mymeerkat/react-form-helper'

export const UPDATE_FUNERAL_BENEFICIARY_PERCENTAGES = gql`
  mutation UpdateFuneralBeneficiaryPercentages($id: String!, $input: UpdateFuneralInput!) {
    updateFuneral(id: $id, input: $input) {
      id
      beneficiaries {
        id
        payoutType
        percentage
        payToId
        payTo {
          id
          firstNames
          surname
        }
      }
    }
  }`

// New beneficiary defaults to an even share of the total (100 / (existing + 1)),
// and the existing beneficiaries are scaled down proportionally (keeping their
// relative split to each other) to make up the rest.
export const computeSmartDefaults = (existingBeneficiaries) => {
  const n = existingBeneficiaries.length
  if (n === 0) return { newPercentage: 100, existingPercentages: [] }

  const newPercentage = Math.round((100 / (n + 1)) * 100) / 100
  const remaining = Math.round((100 - newPercentage) * 100) / 100
  const existingTotal = existingBeneficiaries.reduce((sum, b) => sum + (b.percentage || 0), 0)

  const scaled = existingBeneficiaries.map(b => {
    const share = existingTotal > 0 ? (b.percentage || 0) / existingTotal : 1 / n
    return Math.round(share * remaining * 100) / 100
  })

  const scaledTotal = scaled.reduce((sum, v) => sum + v, 0)
  const diff = Math.round((remaining - scaledTotal) * 100) / 100
  if (diff !== 0) {
    scaled[scaled.length - 1] = Math.round((scaled[scaled.length - 1] + diff) * 100) / 100
  }

  return { newPercentage, existingPercentages: scaled }
}

export const percentageInitialValues = (existingBeneficiaries) => {
  const { newPercentage, existingPercentages } = computeSmartDefaults(existingBeneficiaries)
  return {
    beneficiaries: [{ percentage: newPercentage }],
    existingPercentages: existingPercentages.map(percentage => ({ percentage })),
  }
}

export const sumPercentages = (allValues, existingBeneficiaries, newValue) => {
  const edited = (allValues && allValues.existingPercentages) || []
  const existingTotal = existingBeneficiaries.reduce((sum, b, index) => {
    const editedValue = edited[index] && edited[index].percentage
    const value = editedValue != null && editedValue !== '' ? editedValue : b.percentage
    return sum + (Number(value) || 0)
  }, 0)
  return existingTotal + (Number(newValue) || 0)
}

export const buildPercentageFields = (existingBeneficiaries = []) => {
  const fields = {
    'beneficiaries[0].percentage': {
      type: 'number',
      label: 'Percentage (%)',
      validate: (value, allValues) => {
        if (value == null || value === '') return 'Percentage is required'
        if (value <= 0 || value > 100) return 'Must be between 1 and 100'
        const total = sumPercentages(allValues, existingBeneficiaries, value)
        if (total !== 100) return `Percentages must add up to 100% (currently ${total}%)`
      },
    },
  }

  existingBeneficiaries.forEach((b, index) => {
    const name = b.payTo ? `${b.payTo.firstNames} ${b.payTo.surname}` : `Beneficiary ${index + 1}`
    fields[`existingPercentages[${index}].percentage`] = {
      type: 'number',
      label: `${name} (%)`,
      validate: (value) => {
        if (value == null || value === '') return 'Percentage is required'
        if (value <= 0 || value > 100) return 'Must be between 1 and 100'
      },
    }
  })

  return fields
}

export const cleanExistingBeneficiaries = (existingBeneficiaries, existingPercentages = []) =>
  existingBeneficiaries.map((b, index) => {
    const editedValue = existingPercentages[index] && existingPercentages[index].percentage
    return {
      id: b.id,
      payToId: b.payToId,
      percentage: editedValue != null && editedValue !== '' ? Number(editedValue) : b.percentage,
      payoutType: b.payoutType,
      bankAccountId: b.bankAccount ? b.bankAccount.id : undefined,
    }
  })

export const PercentageSummary = ({ existingBeneficiaries }) => {
  const names = [
    'beneficiaries[0].percentage',
    ...existingBeneficiaries.map((b, index) => `existingPercentages[${index}].percentage`),
  ]

  return (
    <FieldValues
      names={names}
      renderValues={({ values }) => {
        const total = names.reduce((sum, name) => sum + (Number(values[name]) || 0), 0)
        return (
          <div style={{ marginBottom: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 600 }}>Total: {total}%</div>
            {total !== 100 && (
              <div style={{ color: '#c0392b', marginTop: 4 }}>
                Beneficiary percentages must add up to 100% before you can save
              </div>
            )}
          </div>
        )
      }}
    />
  )
}
